// client/src/coms/components/mobile/MobileNavBar.js
// Phones: the navigation bar (Personal Settings → Mobile Layout), at the bottom or as a row under the header.
// Shows the user's destinations in their order, as app icons, emoji, words or a mix; every destination reuses the
// same views and actions as the rest of the app.
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MOBILE_NAV_BY_ID, barItems } from '../../personalisation/mobileLayout';
import NavIcon from './NavIcon';
import './mobile.css';

// Which destination an app screen belongs to.
const DEST_FOR_TAB = { chats: 'chats', coms: 'chat', social: 'dms', games: 'activities', watch: 'watch', canvas: 'canvas', studio: 'studio', maps: 'maps', library: 'files', search: 'search', marketplace: 'marketplace', settings: 'settings' };

// The destination that represents the current screen, among those the user has in the bar: a channel belongs to
// Channel if present, else Servers; a screen whose destination is not in the bar belongs to More.
export function currentDestination({ activeTab, leftOpen, rightOpen, moreOpen, items = [], settingsCategory = '' }) {
  if (moreOpen) return 'more';
  if (leftOpen) return 'servers';
  if (rightOpen) return items.includes('members') ? 'members' : 'chat';
  let dest = DEST_FOR_TAB[activeTab] || '';
  if (dest === 'chat' && !items.includes('chat')) dest = 'servers';
  if (dest === 'settings' && settingsCategory === 'account' && items.includes('profile')) dest = 'profile';
  if (!dest) return '';
  return items.includes(dest) ? dest : items.includes('more') ? 'more' : '';
}

export function navItemIsActive(id, state) {
  return currentDestination(state) === id;
}

// One destination drawn in the chosen style: app icon, emoji, words, or an icon/emoji with a label.
export function NavGlyph({ id, variant, size = 22, badge }) {
  const item = MOBILE_NAV_BY_ID[id];
  if (!item) return null;
  const icon = variant === 'labels' ? null : variant.startsWith('emoji') ? <span className="vx-mnav-emoji">{item.icon}</span> : <NavIcon id={id} size={size} />;
  const label = variant === 'icons' || variant === 'emoji' ? null : <span className="vx-mnav-label">{item.label}</span>;
  return (
    <>
      {icon ? (
        <span className="vx-mnav-icon" aria-hidden="true">
          {icon}
          {badge ? <span className="vx-mnav-badge">{badge === true ? '' : badge}</span> : null}
        </span>
      ) : null}
      {label}
      {!icon && badge ? <span className="vx-mnav-badge is-inline">{badge === true ? '' : badge}</span> : null}
    </>
  );
}

// The bar's height is published (bottom bars only) as --vx-mnav-h so side drawers, the scrim and sheets stop above
// it: the bar stays reachable (and closes a drawer) whatever is open.
export function usePublishedHeight(ref, enabled = true, name = '--vx-mnav-h') {
  useLayoutEffect(() => {
    const el = ref.current;
    const root = document.documentElement;
    if (!el || !enabled) return undefined;
    const set = () => root.style.setProperty(name, `${Math.round(el.getBoundingClientRect().height)}px`);
    set();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(set) : null;
    if (ro) ro.observe(el);
    return () => {
      if (ro) ro.disconnect();
      root.style.removeProperty(name);
    };
  }, [ref, enabled, name]);
}

// "Compact when inactive": the bar shrinks to small icons a few seconds after the last touch and grows back on
// the next one (it stays usable while small).
function useDozing(enabled, ref) {
  const [dozing, setDozing] = useState(false);
  useEffect(() => {
    if (!enabled) {
      setDozing(false);
      return undefined;
    }
    let t = 0;
    const wake = () => {
      setDozing(false);
      clearTimeout(t);
      t = setTimeout(() => setDozing(true), 3500);
    };
    wake();
    const el = ref.current;
    if (el) el.addEventListener('pointerdown', wake);
    return () => {
      clearTimeout(t);
      if (el) el.removeEventListener('pointerdown', wake);
    };
  }, [enabled, ref]);
  return dozing;
}

export default function MobileNavBar({
  nav,
  position = 'bottom',
  style = 'icons-labels',
  size = 'regular',
  overflow = 'more',
  behaviour = 'always',
  hidden = false, // auto-hide: currently scrolled away
  activeTab,
  leftOpen,
  rightOpen,
  moreOpen,
  onNavigate,
  badges = {},
  publish = true, // false for previews: only the real bar tells drawers where to stop
}) {
  const ref = useRef(null);
  const bottom = position === 'bottom';
  usePublishedHeight(ref, publish && bottom && !hidden);
  const dozing = useDozing(behaviour === 'compact', ref);
  const items = barItems(nav, overflow);
  const current = currentDestination({ activeTab, leftOpen, rightOpen, moreOpen, items });
  const shownStyle = dozing ? (style.startsWith('emoji') ? 'emoji' : style === 'labels' ? 'labels' : 'icons') : style;
  const scroller = overflow === 'scroll';

  // A scrolling bar keeps the current destination in view.
  useEffect(() => {
    if (!scroller || !ref.current) return;
    const bar = ref.current;
    const on = bar.querySelector('.vx-mnav-item.is-on');
    if (!on) return;
    // Sideways only, and only when the current one is out of view (scrollIntoView could move the page too).
    const left = on.offsetLeft - bar.offsetLeft;
    if (left < bar.scrollLeft) bar.scrollLeft = Math.max(0, left - 24);
    else if (left + on.offsetWidth > bar.scrollLeft + bar.clientWidth) bar.scrollLeft = left + on.offsetWidth - bar.clientWidth + 24;
  }, [scroller, current]);

  return (
    <nav
      ref={ref}
      className={[
        'vx-mnav',
        `is-${position}`,
        `is-${size}`,
        `is-style-${shownStyle}`,
        scroller ? 'is-scroll' : '',
        dozing ? 'is-dozing' : '',
        hidden ? 'is-hidden' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={publish ? 'Main navigation' : undefined}
      data-testid={publish ? 'mobile-nav' : undefined}
      data-position={position}
      // A scrolling row must not open the side drawers when swiped.
      data-coms-no-swipe={scroller ? 'true' : undefined}
    >
      {items.map((id) => {
        if (!MOBILE_NAV_BY_ID[id]) return null;
        const active = current === id;
        return (
          <button
            key={id}
            type="button"
            className={`vx-mnav-item${active ? ' is-on' : ''}`}
            aria-current={active ? 'page' : undefined}
            aria-label={MOBILE_NAV_BY_ID[id].label}
            onClick={() => onNavigate(id)}
            data-testid={publish ? `mobile-nav-${id}` : undefined}
            tabIndex={hidden ? -1 : undefined}
          >
            <NavGlyph id={id} variant={shownStyle} size={size === 'large' ? 26 : size === 'compact' ? 20 : 22} badge={badges[id]} />
          </button>
        );
      })}
    </nav>
  );
}
